<template>
  <v-dialog
    v-model="dialogModel"
    fullscreen
    transition="dialog-bottom-transition"
    content-class="assistant-dialog"
  >
    <v-card :class="['assistant-shell', { 'ag-dark': isDark }]" tile>
      <!-- Ambient background effects -->
      <div class="ag-orb ag-orb--one"></div>
      <div class="ag-orb ag-orb--two"></div>
      <div class="ag-orb ag-orb--three"></div>

      <!-- ─── Header ─── -->
      <header class="ag-header">
        <div class="ag-header-left">
          <div class="ag-avatar ag-avatar--brand">
            <div class="ag-avatar-ring"></div>
            <v-icon size="22" color="#fff">mdi-robot-happy-outline</v-icon>
          </div>

          <div class="ag-header-info">
            <div class="ag-header-title">
              Atmo Guia
              <span class="ag-status-dot"></span>
            </div>
            <div class="ag-header-context">{{ currentContext }}</div>
          </div>
        </div>

        <div class="ag-header-right">
          <button
            class="ag-header-btn ag-header-btn--clear d-none d-sm-inline-flex"
            @click="handleClearConversation"
          >
            <v-icon small color="inherit">mdi-delete-sweep-outline</v-icon>
            <span>Limpar</span>
          </button>

          <button
            class="ag-header-btn ag-header-btn--close"
            @click="handleCloseDialog"
          >
            <v-icon size="20" color="inherit">mdi-close</v-icon>
          </button>
        </div>
      </header>

      <!-- ─── Main Content ─── -->
      <div class="ag-body">
        <!-- Sidebar -->
        <aside class="ag-sidebar d-none d-md-flex">
          <div class="ag-sidebar-section">
            <div class="ag-sidebar-label">
              <v-icon x-small color="#5b8db5" class="mr-1">mdi-lightning-bolt</v-icon>
              Perguntas rapidas
            </div>

            <button
              v-for="question in quickQuestions"
              :key="question"
              class="ag-quick-btn"
              @click="selectQuestion(question)"
            >
              <v-icon small color="#2b81d6" class="ag-quick-btn-icon">mdi-arrow-right-circle-outline</v-icon>
              <span>{{ question }}</span>
            </button>
          </div>

          <div class="ag-sidebar-footer">
            <div class="ag-shortcut">
              <v-icon x-small color="#5b8db5" class="mr-1">mdi-keyboard</v-icon>
              <kbd>Ctrl</kbd> + <kbd>K</kbd>
              <span class="ag-shortcut-label">para abrir</span>
            </div>
          </div>
        </aside>

        <!-- Chat -->
        <section class="ag-chat">
          <div ref="messagesViewport" class="ag-messages" @click="handleMessageActions">
            <!-- Welcome Hero (when only welcome msg) -->
            <div
              v-if="assistantStore.messages.length <= 1"
              class="ag-welcome"
            >
              <div class="ag-welcome-avatar">
                <div class="ag-welcome-avatar-ring"></div>
                <div class="ag-welcome-avatar-ring ag-welcome-avatar-ring--delayed"></div>
                <div class="ag-welcome-avatar-inner">
                  <v-icon size="44" color="#fff">mdi-robot-happy-outline</v-icon>
                </div>
              </div>

              <h2 class="ag-welcome-title">
                Ola! Eu sou o <span class="ag-gradient-text">Atmo Guia</span>
              </h2>
              <p class="ag-welcome-desc">
                Seu assistente inteligente para navegar o sistema Atmo.<br />
                Pergunte sobre regras, telas, fluxos e funcionalidades.
              </p>

              <div class="ag-welcome-chips d-flex d-md-none">
                <button
                  v-for="question in quickQuestions"
                  :key="'wq-' + question"
                  class="ag-welcome-chip"
                  @click="selectQuestion(question)"
                >
                  {{ question }}
                </button>
              </div>
            </div>

            <!-- Messages -->
            <div
              v-for="message in assistantStore.messages"
              v-show="!(message.streaming && !message.text)"
              :key="message.id"
              :class="[
                'ag-msg',
                message.role === 'user' ? 'ag-msg--user' : 'ag-msg--bot',
                message.streaming ? 'ag-msg--streaming' : '',
              ]"
            >
              <!-- Bot avatar -->
              <div
                v-if="message.role !== 'user'"
                class="ag-msg-avatar ag-msg-avatar--bot"
              >
                <v-icon size="16" color="#fff">mdi-robot-happy-outline</v-icon>
              </div>

              <div class="ag-msg-content">
                <div class="ag-msg-meta">
                  <span class="ag-msg-name">
                    {{ message.role === "user" ? "Voce" : "Atmo Guia" }}
                  </span>
                </div>
                <div class="ag-msg-text">
                  <span v-html="formatMessageText(message.text)"></span>
                  <span v-if="message.streaming" class="ag-stream-cursor"></span>
                </div>
              </div>

              <!-- User avatar -->
              <div
                v-if="message.role === 'user'"
                class="ag-msg-avatar ag-msg-avatar--user"
              >
                <v-icon size="16" color="#fff">mdi-account</v-icon>
              </div>
            </div>

            <!-- Typing indicator -->
            <div
              v-if="assistantStore.isLoading && !hasActiveStreamingText"
              class="ag-msg ag-msg--bot"
            >
              <div class="ag-msg-avatar ag-msg-avatar--bot">
                <v-icon size="16" color="#fff">mdi-robot-happy-outline</v-icon>
              </div>
              <div class="ag-msg-content">
                <div class="ag-msg-meta">
                  <span class="ag-msg-name">Atmo Guia</span>
                </div>
                <div v-if="assistantStore.loadingStatus" class="ag-loading-status">
                  <div class="ag-loading-spinner"></div>
                  <span>{{ assistantStore.loadingStatus }}</span>
                </div>
                <div v-else class="ag-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Input -->
          <div class="ag-input-bar">
            <div class="ag-input-wrap">
              <v-textarea
                v-model="draft"
                class="ag-input"
                auto-grow
                rows="1"
                row-height="20"
                outlined
                hide-details
                no-resize
                placeholder="Pergunte sobre regras, fluxos e telas do Atmo..."
                @keydown.enter.exact.prevent="sendMessage"
              ></v-textarea>

              <button
                v-if="assistantStore.isLoading"
                class="ag-stop-btn"
                type="button"
                @click="stopGenerating"
              >
                <v-icon size="19" color="#fff">mdi-stop</v-icon>
              </button>

              <button
                v-else
                class="ag-send-btn"
                :class="{ 'ag-send-btn--active': canSend }"
                :disabled="!canSend"
                type="button"
                @click="sendMessage"
              >
                <v-icon size="20" :color="canSend ? '#fff' : '#8fbad6'">
                  mdi-send-variant
                </v-icon>
              </button>
            </div>
            <div class="ag-input-hint">
              <span>
                {{ assistantStore.isLoading ? "Clique em Parar para interromper" : "Enter para enviar" }}
              </span>
            </div>
          </div>
        </section>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import axios from "axios";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { useIp, useRoute, useVuetify } from "@/composables";
import { useAssistantStore, useAuthStore, useSystemStore } from "@/stores";

const assistantStore = useAssistantStore();
const authStore = useAuthStore();
const systemStore = useSystemStore();
const ips = useIp();
const route = useRoute();
const vuetify = useVuetify();

const isDark = computed(() => vuetify?.theme?.dark ?? false);

const messagesViewport = ref(null);
const draft = ref("");
const requestAbortController = ref(null);
const requestCancelSource = ref(null);
const requestSequence = ref(0);
const activeRequestId = ref(null);
const assistantOwnerId = ref(null);

const moduleLabelByKey = {
  security: "Seguranca",
  masterdata: "Cadastros",
  proposals: "Propostas",
  purchases: "Compras",
  approvalflow: "Fluxo de Aprovacao",
  stock: "Estoque",
  default: "Portal",
};

const moduleSuggestions = {
  security: [
    "Qual permissao libera esta tela?",
    "Qual diferenca entre grupo e perfil de acesso?",
    "Como rastrear acessos de um usuario?",
  ],
  masterdata: [
    "Quais campos sao obrigatorios neste cadastro?",
    "Como esta tela impacta Compras e Propostas?",
    "Quais validacoes existem antes de salvar?",
  ],
  proposals: [
    "Qual e o fluxo da proposta ate aprovacao?",
    "Como funciona o orcamento aberto e fechado?",
    "Quando ocorre integracao com OMIE?",
  ],
  purchases: [
    "Qual diferenca entre requisicao e pedido?",
    "Como funciona a aprovacao de compras?",
    "Quais regras de status desta tela?",
  ],
  approvalflow: [
    "Como os niveis de aprovacao sao definidos?",
    "Qual regra bloqueia aprovacao automatica?",
    "Como auditar historico de aprovacoes?",
  ],
  stock: [
    "Qual regra do checklist de embarque?",
    "Como uma movimentacao vai para aprovacao?",
    "Quais status afetam o estoque disponivel?",
  ],
  default: [
    "Qual objetivo desta tela?",
    "Quais regras criticas eu preciso lembrar aqui?",
    "Quais permissoes controlam este fluxo?",
  ],
};

const dialogModel = computed({
  get: () => assistantStore.isOpen,
  set: (value) => (value ? assistantStore.open() : assistantStore.close()),
});

const currentModule = computed(() => route.path?.split("/")?.[1] || "default");

const currentContext = computed(() => {
  const moduleName =
    moduleLabelByKey[currentModule.value] || moduleLabelByKey.default;
  const routeName = route.name ? String(route.name) : route.path;
  return `${moduleName} - ${routeName}`;
});

const quickQuestions = computed(() => {
  return moduleSuggestions[currentModule.value] || moduleSuggestions.default;
});

const canSend = computed(() => {
  return draft.value.trim().length > 0 && !assistantStore.isLoading;
});

const hasActiveStreamingText = computed(() => {
  return assistantStore.messages.some((m) => m.streaming && m.text);
});

function scrollToBottom() {
  if (!messagesViewport.value) return;
  messagesViewport.value.scrollTop = messagesViewport.value.scrollHeight;
}

function resetRequestControlState() {
  requestAbortController.value = null;
  requestCancelSource.value = null;
}

function isRequestCanceled(err) {
  if (typeof axios.isCancel === "function" && axios.isCancel(err)) return true;

  return (
    err?.code === "ERR_CANCELED" ||
    err?.message === "canceled" ||
    err?.name === "AbortError" ||
    err?.__CANCEL__ === true
  );
}

function cancelPendingRequest() {
  let wasCanceled = false;

  if (requestCancelSource.value) {
    requestCancelSource.value.cancel("canceled");
    wasCanceled = true;
  }
  if (requestAbortController.value) {
    requestAbortController.value.abort();
    wasCanceled = true;
  }

  return wasCanceled;
}

function getAppOrigin() {
  if (typeof window === "undefined") return "";
  return String(window.location.origin || "").replace(/\/+$/, "");
}

function getPathname(urlOrPath) {
  const value = String(urlOrPath || "").trim();
  if (!value) return "";

  if (value.startsWith("/")) return value;

  try {
    const parsed = new URL(value);
    return parsed.pathname || "";
  } catch (_err) {
    return "";
  }
}

function hasDynamicRouteParam(urlOrPath) {
  const pathname = getPathname(urlOrPath);
  return /\/:[a-z0-9_-]+/i.test(pathname);
}

function isContextualQueryRoute(pathname) {
  const normalized = String(pathname || "").toLowerCase();
  return [
    "/stock/list-editor",
    "/stock/list-editor-checklist",
    "/purchases/list-editor-requisition",
    "/purchases/list-editor-purchase-order",
  ].includes(normalized);
}

function hasRequiredRouteContext(urlOrPath) {
  const value = String(urlOrPath || "").trim();
  if (!value) return false;

  try {
    const base = getAppOrigin() || "http://localhost";
    const parsed = value.startsWith("/")
      ? new URL(`${base}${value}`)
      : new URL(value);

    if (!isContextualQueryRoute(parsed.pathname)) return true;
    return parsed.searchParams.toString().length > 0;
  } catch (_err) {
    return true;
  }
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function splitTrailingPunctuation(value) {
  const match = String(value || "").match(/^(.*?)([.,!?;:]+)?$/);
  return {
    core: match?.[1] || "",
    trailing: match?.[2] || "",
  };
}

function normalizeLink(rawHref) {
  const href = String(rawHref || "").trim();
  if (!href) return null;

  if (/^https?:\/\//i.test(href)) return href;
  if (/^www\./i.test(href)) return `https://${href}`;
  if (href.startsWith("/")) {
    const origin = getAppOrigin();
    return origin ? `${origin}${href}` : href;
  }

  return null;
}

function createAnchor(label, href) {
  const safeHref = normalizeLink(href);
  if (!safeHref || hasDynamicRouteParam(safeHref) || !hasRequiredRouteContext(safeHref)) {
    return escapeHtml(label);
  }

  return `<a href="${escapeHtml(safeHref)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`;
}

function toBase64Utf8(value) {
  try {
    if (typeof btoa !== "function") return "";
    return btoa(unescape(encodeURIComponent(String(value || ""))));
  } catch (_err) {
    return "";
  }
}

function fromBase64Utf8(value) {
  try {
    if (typeof atob !== "function") return "";
    return decodeURIComponent(escape(atob(String(value || ""))));
  } catch (_err) {
    return "";
  }
}

async function copyText(text) {
  const normalized = String(text || "");
  if (!normalized) return;

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(normalized);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = normalized;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function buildFileCardHtml(type, url, name) {
  const safeUrl = escapeHtml(String(url || "").trim());
  const safeName = escapeHtml(String(name || "Arquivo").trim());
  const fileType = String(type || "").toLowerCase().trim();

  if (!safeUrl) return "";

  const icon = fileType === "pdf" ? "mdi-file-pdf-box" : "mdi-microsoft-excel";
  const iconColor = fileType === "pdf" ? "#e53935" : "#2e7d32";
  const label = fileType === "pdf" ? "PDF" : "Excel";

  const previewHtml = buildFilePreviewHtml(fileType, safeUrl);

  return [
    '<div class="ag-file-card">',
    previewHtml,
    '  <div class="ag-file-card-info">',
    '    <div class="ag-file-card-meta">',
    `      <span class="ag-file-card-icon" style="color:${iconColor}">`,
    `        <i class="v-icon mdi ${icon}" style="font-size:22px;color:${iconColor}"></i>`,
    "      </span>",
    `      <span class="ag-file-card-name">${safeName}</span>`,
    `      <span class="ag-file-card-badge">${escapeHtml(label)}</span>`,
    "    </div>",
    '    <div class="ag-file-card-actions">',
    `      <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="ag-file-card-btn ag-file-card-btn--download" download>`,
    '        <i class="v-icon mdi mdi-download" style="font-size:16px"></i> Baixar',
    "      </a>",
    `      <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="ag-file-card-btn ag-file-card-btn--open">`,
    '        <i class="v-icon mdi mdi-open-in-new" style="font-size:16px"></i> Abrir',
    "      </a>",
    "    </div>",
    "  </div>",
    "</div>",
  ].join("");
}

function buildFilePreviewHtml(fileType, safeUrl) {
  if (fileType === "pdf") {
    const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(safeUrl)}&embedded=true`;
    return `<div class="ag-file-card-preview"><iframe src="${escapeHtml(googleViewerUrl)}" class="ag-file-card-iframe" loading="lazy"></iframe></div>`;
  }

  if (fileType === "excel") {
    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(safeUrl)}`;
    return `<div class="ag-file-card-preview"><iframe src="${escapeHtml(viewerUrl)}" class="ag-file-card-iframe" loading="lazy"></iframe></div>`;
  }

  return "";
}

function buildCodeBlockHtml(code, language) {
  const codeText = String(code || "").replace(/\r\n/g, "\n").replace(/\n$/, "");
  const encoded = toBase64Utf8(codeText);
  const lang = String(language || "").trim() || "texto";

  return [
    '<div class="ag-code-block">',
    '  <div class="ag-code-head">',
    `    <span class="ag-code-lang">${escapeHtml(lang)}</span>`,
    `    <button type="button" class="ag-code-copy-btn" data-copy="${escapeHtml(encoded)}">Copiar</button>`,
    "  </div>",
    `  <pre class="ag-code-pre"><code>${escapeHtml(codeText)}</code></pre>`,
    "</div>",
  ].join("");
}

function formatMessageText(text) {
  const placeholders = [];
  const stash = (html) => {
    const token = `__AG_LINK_${placeholders.length}__`;
    placeholders.push(html);
    return token;
  };

  const codeBlocks = [];
  const stashCodeBlock = (html) => {
    const token = `__AG_CODE_${codeBlocks.length}__`;
    codeBlocks.push(html);
    return token;
  };

  const fileCards = [];
  const stashFileCard = (html) => {
    const token = `__AG_FILE_${fileCards.length}__`;
    fileCards.push(html);
    return token;
  };

  // File cards: :::file{"type":"excel","url":"...","name":"..."}:::
  let textWithFileTokens = String(text || "").replace(
    /:::file\{([\s\S]*?)\}:::/g,
    (match, jsonContent) => {
      try {
        const parsed = JSON.parse(`{${jsonContent}}`);
        const html = buildFileCardHtml(parsed.type, parsed.url, parsed.name);
        return html ? stashFileCard(html) : match;
      } catch (_err) {
        return match;
      }
    }
  );

  const textWithCodeTokens = textWithFileTokens.replace(
    /```([a-zA-Z0-9_-]+)?\n?([\s\S]*?)```/g,
    (match, language, code) => stashCodeBlock(buildCodeBlockHtml(code, language))
  );

  let formatted = escapeHtml(textWithCodeTokens);

  // Markdown links: [texto](url)
  formatted = formatted.replace(
    /\[([^\]\n]{1,200})\]\(([^)\s]+)\)/g,
    (match, label, href) => {
      const linked = createAnchor(label, href);
      return linked === escapeHtml(label) ? match : stash(linked);
    }
  );

  // Bare URLs
  formatted = formatted.replace(
    /(^|[\s(])((?:https?:\/\/|www\.)[^\s)]+)/gi,
    (match, prefix, rawUrl) => {
      const { core, trailing } = splitTrailingPunctuation(rawUrl);
      if (!core) return match;
      return `${prefix}${stash(createAnchor(core, core))}${trailing}`;
    }
  );

  // Atmo route paths
  formatted = formatted.replace(
    /(^|[\s(])((?:\/(?:security|masterdata|proposals|purchases|approvalflow|stock)[^\s)<]*)+)/gi,
    (match, prefix, rawRoute) => {
      const { core, trailing } = splitTrailingPunctuation(rawRoute);
      if (!core) return match;
      return `${prefix}${stash(createAnchor(core, core))}${trailing}`;
    }
  );

  formatted = formatted.replace(/\n/g, "<br>");
  formatted = formatted.replace(/__AG_LINK_(\d+)__/g, (match, index) => {
    return placeholders[Number(index)] || match;
  });
  formatted = formatted.replace(/__AG_CODE_(\d+)__/g, (match, index) => {
    return codeBlocks[Number(index)] || match;
  });
  formatted = formatted.replace(/__AG_FILE_(\d+)__/g, (match, index) => {
    return fileCards[Number(index)] || match;
  });

  return formatted;
}

async function handleMessageActions(event) {
  const copyButton = event?.target?.closest?.(".ag-code-copy-btn");
  if (copyButton) {
    const encoded = copyButton.getAttribute("data-copy") || "";
    const textToCopy = fromBase64Utf8(encoded);
    if (!textToCopy) return;

    try {
      await copyText(textToCopy);
      const previousLabel = copyButton.textContent;
      copyButton.textContent = "Copiado";
      setTimeout(() => {
        if (copyButton) copyButton.textContent = previousLabel || "Copiar";
      }, 1200);
    } catch (_err) {
      copyButton.textContent = "Falha";
      setTimeout(() => {
        if (copyButton) copyButton.textContent = "Copiar";
      }, 1200);
    }
    return;
  }

  const anchor = event?.target?.closest?.("a");
  if (!anchor) return;

  cancelPendingRequest();
  resetRequestControlState();
  activeRequestId.value = null;
  assistantStore.setLoading(false);
  assistantStore.close();
}

function normalizeUserId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function ensureAssistantOwnership() {
  const currentUserId = normalizeUserId(authStore.id);
  if (!currentUserId) return;
  if (!assistantOwnerId.value) {
    assistantOwnerId.value = currentUserId;
    return;
  }
  if (assistantOwnerId.value === currentUserId) return;

  cancelPendingRequest();
  resetRequestControlState();
  activeRequestId.value = null;
  assistantStore.setLoading(false);
  assistantStore.clearConversation();
  assistantOwnerId.value = currentUserId;
}

function selectQuestion(question) {
  draft.value = question;
  sendMessage();
}

function mapConversationToPayload() {
  return assistantStore.messages
    .map((message) => ({
      role: message.role,
      content: message.text,
    }))
    .filter((message) => message.role === "user" || message.role === "assistant")
    .slice(-20);
}

const EXPORT_KEYWORDS = ["export", "baixa", "gera", "excel", "pdf", "planilha", "documento"];
const EXPORT_STATUS_STEPS = [
  { text: "Analisando solicitacao...", delay: 0 },
  { text: "Buscando dados da proposta...", delay: 2000 },
  { text: "Gerando documento...", delay: 5000 },
  { text: "Fazendo upload do arquivo...", delay: 9000 },
];

function looksLikeExportRequest(text) {
  const normalized = String(text || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return EXPORT_KEYWORDS.some((kw) => normalized.includes(kw));
}

let statusTimers = [];

function startExportStatusUpdates(requestId) {
  clearExportStatusTimers();
  for (const step of EXPORT_STATUS_STEPS) {
    const timer = setTimeout(() => {
      if (activeRequestId.value === requestId && assistantStore.isLoading) {
        assistantStore.setLoadingStatus(step.text);
      }
    }, step.delay);
    statusTimers.push(timer);
  }
}

function clearExportStatusTimers() {
  for (const timer of statusTimers) clearTimeout(timer);
  statusTimers = [];
}

async function sendMessage() {
  const text = draft.value.trim();
  if (!text || assistantStore.isLoading) return;
  const requestId = ++requestSequence.value;

  assistantStore.addUserMessage(text);
  draft.value = "";
  assistantStore.setLoading(true);
  activeRequestId.value = requestId;
  requestAbortController.value =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  requestCancelSource.value = null;

  if (looksLikeExportRequest(text)) {
    startExportStatusUpdates(requestId);
  }

  nextTick(scrollToBottom);

  const streamingId = assistantStore.startStreamingAssistantMessage();
  let receivedFirstDelta = false;
  let streamErrored = false;

  try {
    const payload = {
      messages: mapConversationToPayload(),
      context: {
        module: currentModule.value,
        route: route.path,
        routeName: route.name ? String(route.name) : null,
        origin: getAppOrigin(),
      },
      language: systemStore.language === "pt_BR" ? "pt" : systemStore.language,
      clientId: systemStore.clientId,
      system: systemStore.system,
    };

    const headers = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    if (authStore.token) headers.Authorization = `Bearer ${authStore.token}`;
    if (systemStore.clientId) headers["app-client-id"] = String(systemStore.clientId);
    if (systemStore.module) headers["app-module"] = String(systemStore.module);

    const response = await fetch(`${ips.ipUser}assistant/chat/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      signal: requestAbortController.value?.signal,
    });

    if (!response.ok || !response.body) {
      let errMsg = `Erro ${response.status}`;
      try {
        const errJson = await response.json();
        if (errJson?.msg) errMsg = errJson.msg;
      } catch (_e) { /* noop */ }
      throw new Error(errMsg);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    let streamDone = false;
    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) { streamDone = true; break; }
      if (activeRequestId.value !== requestId) {
        try { reader.cancel(); } catch (_e) { /* noop */ }
        return;
      }

      buffer += decoder.decode(value, { stream: true });

      let separatorIndex;
      while ((separatorIndex = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);

        let eventName = "message";
        let dataPayload = "";
        for (const line of rawEvent.split("\n")) {
          if (line.startsWith("event:")) eventName = line.slice(6).trim();
          else if (line.startsWith("data:")) dataPayload += line.slice(5).trim();
        }
        if (!dataPayload) continue;

        let data;
        try { data = JSON.parse(dataPayload); }
        catch (_e) { continue; }

        if (eventName === "delta" && data?.text) {
          if (!receivedFirstDelta) {
            receivedFirstDelta = true;
            clearExportStatusTimers();
            assistantStore.setLoadingStatus("");
          }
          assistantStore.appendToMessage(streamingId, data.text);
          nextTick(scrollToBottom);
        } else if (eventName === "status" && data?.text) {
          assistantStore.setLoadingStatus(data.text);
        } else if (eventName === "replace" && typeof data?.text === "string") {
          assistantStore.replaceMessage(streamingId, data.text);
          nextTick(scrollToBottom);
        } else if (eventName === "error") {
          streamErrored = true;
          assistantStore.replaceMessage(
            streamingId,
            data?.message || "Nao consegui responder agora."
          );
        } else if (eventName === "done") {
          // finalize after loop
        }
      }
    }
  } catch (err) {
    if (activeRequestId.value !== requestId) return;
    if (isRequestCanceled(err)) {
      assistantStore.removeMessage(streamingId);
      return;
    }
    streamErrored = true;
    const errorMessage =
      err?.message ||
      "Nao consegui responder agora. Verifique a configuracao do provider e tente novamente.";
    assistantStore.replaceMessage(streamingId, errorMessage);
  } finally {
    if (activeRequestId.value === requestId) {
      clearExportStatusTimers();
      resetRequestControlState();
      assistantStore.setLoading(false);
      assistantStore.finalizeStreamingMessage(streamingId);
      if (!receivedFirstDelta && !streamErrored) {
        const stillEmpty = assistantStore.messages.find((m) => m.id === streamingId);
        if (stillEmpty && !stillEmpty.text) {
          assistantStore.replaceMessage(
            streamingId,
            "Nao consegui montar uma resposta valida agora. Tente novamente."
          );
        }
      }
      activeRequestId.value = null;
      nextTick(scrollToBottom);
    }
  }
}

function stopGenerating() {
  if (!assistantStore.isLoading) return;

  clearExportStatusTimers();
  cancelPendingRequest();
  assistantStore.setLoading(false);
  activeRequestId.value = null;
  resetRequestControlState();
  assistantStore.addAssistantMessage(
    "Resposta interrompida. Voce pode enviar uma nova pergunta."
  );
  nextTick(scrollToBottom);
}

function handleClearConversation() {
  clearExportStatusTimers();
  cancelPendingRequest();
  resetRequestControlState();
  activeRequestId.value = null;
  assistantStore.clearConversation();
  assistantStore.setLoading(false);
}

function handleCloseDialog() {
  cancelPendingRequest();
  resetRequestControlState();
  activeRequestId.value = null;
  assistantStore.close();
  assistantStore.setLoading(false);
}

function handleGlobalShortcut(event) {
  const isOpenShortcut =
    (event.ctrlKey || event.metaKey) &&
    event.key &&
    event.key.toLowerCase() === "k";

  if (isOpenShortcut) {
    event.preventDefault();
    assistantStore.open();
  }
}

watch(
  () => assistantStore.messages.length,
  () => {
    nextTick(scrollToBottom);
  }
);

watch(
  () => assistantStore.isOpen,
  (isOpen) => {
    if (!isOpen) return;
    ensureAssistantOwnership();
    nextTick(scrollToBottom);
  }
);

watch(
  () => authStore.id,
  () => {
    ensureAssistantOwnership();
  }
);

onMounted(() => {
  ensureAssistantOwnership();
  window.addEventListener("keydown", handleGlobalShortcut);
});

onBeforeUnmount(() => {
  clearExportStatusTimers();
  cancelPendingRequest();
  resetRequestControlState();
  activeRequestId.value = null;
  window.removeEventListener("keydown", handleGlobalShortcut);
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

/* ═══════════════════════════════════════════════
   DESIGN TOKENS — Azul Atmo (#2b81d6)
   ═══════════════════════════════════════════════ */
.assistant-shell {
  /* Base blues — graduated from brand blue */
  --ag-brand:       #2b81d6;
  --ag-brand-light: #4a9ae8;
  --ag-brand-dark:  #1a5a9e;

  /* Backgrounds — lighter, airy blues */
  --ag-bg:          linear-gradient(145deg, #e8f2fc 0%, #d0e4f7 40%, #b8d6f2 100%);
  --ag-surface:     rgba(255, 255, 255, 0.55);
  --ag-surface-hover: rgba(255, 255, 255, 0.72);

  /* Borders */
  --ag-border:       rgba(43, 129, 214, 0.18);
  --ag-border-hover: rgba(43, 129, 214, 0.35);

  /* Text */
  --ag-text:     #1a3a5c;
  --ag-text-2:   #3d6a96;
  --ag-text-3:   #6a9dc7;

  /* Bubbles */
  --ag-user-bubble:  rgba(43, 129, 214, 0.12);
  --ag-user-border:  rgba(43, 129, 214, 0.25);
  --ag-bot-bubble:   rgba(255, 255, 255, 0.7);
  --ag-bot-border:   rgba(43, 129, 214, 0.15);

  --ag-radius: 16px;

  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  background: var(--ag-bg);
  color: var(--ag-text);
}

/* ═══════════════════════════════════════════════
   BACKGROUND EFFECTS
   ═══════════════════════════════════════════════ */
.ag-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.ag-orb--one {
  width: 600px;
  height: 600px;
  top: -200px;
  left: -200px;
  background: radial-gradient(circle, rgba(43, 129, 214, 0.25), transparent 70%);
  animation: orbDrift 20s ease-in-out infinite;
}

.ag-orb--two {
  width: 500px;
  height: 500px;
  bottom: -200px;
  right: -150px;
  background: radial-gradient(circle, rgba(74, 154, 232, 0.18), transparent 70%);
  animation: orbDrift 25s ease-in-out infinite reverse;
}

.ag-orb--three {
  width: 400px;
  height: 400px;
  top: 50%;
  left: 40%;
  background: radial-gradient(circle, rgba(26, 90, 158, 0.10), transparent 70%);
  animation: orbDrift 18s ease-in-out infinite 3s;
}

@keyframes orbDrift {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25%  { transform: translate(50px, -30px) scale(1.08); }
  50%  { transform: translate(-30px, 40px) scale(0.95); }
  75%  { transform: translate(40px, 20px) scale(1.05); }
}

/* ═══════════════════════════════════════════════
   HEADER
   ═══════════════════════════════════════════════ */
.ag-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(20px) saturate(1.4);
  -webkit-backdrop-filter: blur(20px) saturate(1.4);
  border-bottom: 1px solid var(--ag-border);
}

.ag-header::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--ag-brand), var(--ag-brand-light), transparent);
  opacity: 0.5;
}

.ag-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.ag-avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
}

.ag-avatar--brand {
  background: linear-gradient(135deg, #4a9ae8 0%, #2b81d6 60%, #1a5a9e 100%);
  box-shadow: 0 4px 16px rgba(43, 129, 214, 0.35);
}

.ag-avatar-ring {
  position: absolute;
  inset: -3px;
  border-radius: 17px;
  border: 2px solid transparent;
  background: linear-gradient(135deg, rgba(43, 129, 214, 0.4), rgba(74, 154, 232, 0.3)) border-box;
  -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  animation: ringRotate 8s linear infinite;
}

@keyframes ringRotate {
  to { transform: rotate(360deg); }
}

.ag-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: var(--ag-text);
}

.ag-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.5);
  animation: statusPulse 2s ease-in-out infinite;
}

@keyframes statusPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.85); }
}

.ag-header-context {
  font-size: 12px;
  font-weight: 500;
  color: var(--ag-text-3);
  margin-top: 1px;
}

.ag-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ag-header-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: var(--ag-text-2);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
}

.ag-header-btn:hover {
  background: rgba(43, 129, 214, 0.08);
  border-color: var(--ag-border);
  color: var(--ag-brand);
}

.ag-header-btn--close {
  padding: 8px;
  border-radius: 10px;
}

.ag-header-btn--close:hover {
  background: rgba(239, 68, 68, 0.10);
  border-color: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

/* ═══════════════════════════════════════════════
   BODY LAYOUT
   ═══════════════════════════════════════════════ */
.ag-body {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 280px 1fr;
  flex: 1;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  gap: 14px;
  padding: 14px;
}

/* ═══════════════════════════════════════════════
   SIDEBAR
   ═══════════════════════════════════════════════ */
.ag-sidebar {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--ag-border);
  border-radius: var(--ag-radius);
  background: var(--ag-surface);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 4px 20px rgba(43, 129, 214, 0.08);
  overflow: hidden;
}

.ag-sidebar-section {
  flex: 1;
  padding: 18px 14px;
  overflow-y: auto;
}

.ag-sidebar-label {
  display: flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--ag-text-3);
  margin-bottom: 14px;
}

.ag-quick-btn {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 11px 12px;
  margin-bottom: 6px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: var(--ag-text-2);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
  text-align: left;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.ag-quick-btn-icon {
  margin-top: 2px;
  flex-shrink: 0;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.25s ease;
}

.ag-quick-btn:hover {
  background: rgba(43, 129, 214, 0.08);
  border-color: var(--ag-border-hover);
  color: var(--ag-text);
  transform: translateX(2px);
}

.ag-quick-btn:hover .ag-quick-btn-icon {
  opacity: 1;
  transform: translateX(0);
}

.ag-sidebar-footer {
  padding: 14px;
  border-top: 1px solid var(--ag-border);
}

.ag-shortcut {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: var(--ag-text-3);
  font-weight: 500;
}

.ag-shortcut kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  margin: 0 3px;
  border: 1px solid rgba(43, 129, 214, 0.2);
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.6);
  color: var(--ag-brand);
  font-family: 'Inter', monospace;
  font-size: 10px;
  font-weight: 700;
}

.ag-shortcut-label {
  margin-left: 6px;
  opacity: 0.7;
}

/* ═══════════════════════════════════════════════
   CHAT
   ═══════════════════════════════════════════════ */
.ag-chat {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  border: 1px solid var(--ag-border);
  border-radius: var(--ag-radius);
  background: var(--ag-surface);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 4px 20px rgba(43, 129, 214, 0.08);
  overflow: hidden;
}

/* ═══════════════════════════════════════════════
   WELCOME HERO
   ═══════════════════════════════════════════════ */
.ag-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px 32px;
  animation: welcomeFade 0.6s ease;
}

@keyframes welcomeFade {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

.ag-welcome-avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  margin-bottom: 24px;
}

.ag-welcome-avatar-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 22px;
  background: linear-gradient(135deg, #4a9ae8 0%, #2b81d6 50%, #1a5a9e 100%);
  box-shadow: 0 8px 30px rgba(43, 129, 214, 0.35);
  z-index: 1;
}

.ag-welcome-avatar-ring {
  position: absolute;
  inset: 0;
  border-radius: 28px;
  border: 2px solid rgba(43, 129, 214, 0.25);
  animation: welcomeRing 3s ease-in-out infinite;
}

.ag-welcome-avatar-ring--delayed {
  animation-delay: 1.5s;
  border-color: rgba(74, 154, 232, 0.2);
}

@keyframes welcomeRing {
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.15); opacity: 0; }
}

.ag-welcome-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: var(--ag-text);
  margin: 0 0 10px;
}

.ag-gradient-text {
  background: linear-gradient(135deg, var(--ag-brand-light), var(--ag-brand-dark));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.ag-welcome-desc {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.7;
  color: var(--ag-text-2);
  max-width: 460px;
  margin: 0;
}

.ag-welcome-chips {
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}

.ag-welcome-chip {
  padding: 10px 16px;
  border: 1px solid var(--ag-border-hover);
  border-radius: 100px;
  background: rgba(43, 129, 214, 0.06);
  color: var(--ag-text-2);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
}

.ag-welcome-chip:hover {
  background: rgba(43, 129, 214, 0.14);
  border-color: var(--ag-brand);
  color: var(--ag-text);
}

/* ═══════════════════════════════════════════════
   MESSAGES
   ═══════════════════════════════════════════════ */
.ag-messages {
  flex: 1;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  padding: 20px;
  scroll-behavior: smooth;
}

.ag-messages::-webkit-scrollbar {
  width: 5px;
}
.ag-messages::-webkit-scrollbar-track {
  background: transparent;
}
.ag-messages::-webkit-scrollbar-thumb {
  background: rgba(43, 129, 214, 0.15);
  border-radius: 10px;
}
.ag-messages::-webkit-scrollbar-thumb:hover {
  background: rgba(43, 129, 214, 0.3);
}

.ag-msg {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
  animation: msgSlide 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes msgSlide {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

.ag-msg--bot {
  justify-content: flex-start;
}
.ag-msg--user {
  justify-content: flex-end;
}

.ag-msg-avatar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  margin-top: 2px;
}

.ag-msg-avatar--bot {
  background: linear-gradient(135deg, #4a9ae8, #2b81d6);
  box-shadow: 0 2px 8px rgba(43, 129, 214, 0.3);
}

.ag-msg-avatar--user {
  background: linear-gradient(135deg, #3d6a96, #1a3a5c);
  box-shadow: 0 2px 8px rgba(26, 58, 92, 0.25);
}

.ag-msg-content {
  max-width: min(72%, 700px);
  padding: 12px 16px;
  border-radius: 14px;
}

.ag-msg--bot .ag-msg-content {
  background: var(--ag-bot-bubble);
  backdrop-filter: blur(8px);
  border: 1px solid var(--ag-bot-border);
  border-top-left-radius: 4px;
}

.ag-msg--user .ag-msg-content {
  background: var(--ag-user-bubble);
  backdrop-filter: blur(8px);
  border: 1px solid var(--ag-user-border);
  border-top-right-radius: 4px;
}

.ag-msg-meta {
  margin-bottom: 4px;
}

.ag-msg-name {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.ag-msg--bot .ag-msg-name {
  color: var(--ag-brand);
}
.ag-msg--user .ag-msg-name {
  color: var(--ag-text-3);
}

.ag-msg-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.65;
  color: var(--ag-text);
  white-space: pre-wrap;
}

.ag-msg-text ::v-deep a {
  color: var(--ag-brand-dark);
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 600;
}

.ag-msg-text ::v-deep a:hover {
  color: var(--ag-brand);
}

.ag-msg-text ::v-deep .ag-code-block {
  margin: 10px 0;
  border: 1px solid rgba(26, 58, 92, 0.2);
  border-radius: 10px;
  overflow: hidden;
  background: rgba(17, 24, 39, 0.95);
}

.ag-msg-text ::v-deep .ag-code-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  background: rgba(15, 23, 42, 0.96);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.ag-msg-text ::v-deep .ag-code-lang {
  font-size: 11px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(203, 213, 225, 0.9);
  font-weight: 700;
}

.ag-msg-text ::v-deep .ag-code-copy-btn {
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 700;
  color: #e2e8f0;
  background: rgba(30, 41, 59, 0.9);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.ag-msg-text ::v-deep .ag-code-copy-btn:hover {
  background: rgba(51, 65, 85, 0.95);
  border-color: rgba(125, 211, 252, 0.6);
}

.ag-msg-text ::v-deep .ag-code-pre {
  margin: 0;
  padding: 10px 12px;
  max-height: 280px;
  overflow: auto;
}

.ag-msg-text ::v-deep .ag-code-pre code {
  display: block;
  font-family: Consolas, "Courier New", monospace;
  font-size: 12px;
  line-height: 1.5;
  color: #e5e7eb;
  white-space: pre;
}

/* ═══════════════════════════════════════════════
   FILE CARD
   ═══════════════════════════════════════════════ */
.ag-msg-text ::v-deep .ag-file-card {
  margin: 8px 0;
  border: 1px solid var(--ag-border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--ag-surface);
  max-width: 420px;
}

.ag-msg-text ::v-deep .ag-file-card-preview {
  width: 100%;
  height: 240px;
  overflow: hidden;
  border-bottom: 1px solid var(--ag-border);
  background: #f5f7fa;
  position: relative;
}

.ag-msg-text ::v-deep .ag-file-card-iframe {
  width: 100%;
  height: calc(100% + 50px);
  border: none;
  pointer-events: auto;
}

.ag-msg-text ::v-deep .ag-file-card-info {
  padding: 10px 14px;
}

.ag-msg-text ::v-deep .ag-file-card-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.ag-msg-text ::v-deep .ag-file-card-name {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--ag-text);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ag-msg-text ::v-deep .ag-file-card-badge {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 7px;
  border-radius: 4px;
  background: rgba(43, 129, 214, 0.1);
  color: var(--ag-brand);
}

.ag-msg-text ::v-deep .ag-file-card-actions {
  display: flex;
  gap: 8px;
}

.ag-msg-text ::v-deep .ag-file-card-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s, transform 0.1s;
  cursor: pointer;
}

.ag-msg-text ::v-deep .ag-file-card-btn:hover {
  transform: translateY(-1px);
}

.ag-msg-text ::v-deep .ag-file-card-btn--download {
  background: #1a5a9e;
  color: #fff;
}

.ag-msg-text ::v-deep .ag-file-card-btn--download:hover {
  background: #0e3f72;
}

.ag-msg-text ::v-deep .ag-file-card-btn--open {
  background: rgba(43, 129, 214, 0.1);
  color: var(--ag-brand);
}

.ag-msg-text ::v-deep .ag-file-card-btn--open:hover {
  background: rgba(43, 129, 214, 0.2);
}

/* ═══════════════════════════════════════════════
   TYPING INDICATOR
   ═══════════════════════════════════════════════ */
.ag-typing {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 2px;
}

.ag-typing span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ag-brand);
  animation: typeBounce 1.4s ease-in-out infinite;
}
.ag-typing span:nth-child(2) { animation-delay: 0.2s; }
.ag-typing span:nth-child(3) { animation-delay: 0.4s; }

.ag-stream-cursor {
  display: inline-block;
  width: 8px;
  height: 1em;
  margin-left: 2px;
  vertical-align: text-bottom;
  background: linear-gradient(180deg, #2b81d6, #5b8db5);
  border-radius: 2px;
  animation: agCursorBlink 1s steps(2, start) infinite;
}

@keyframes agCursorBlink {
  to { visibility: hidden; }
}

@keyframes typeBounce {
  0%, 80%, 100% {
    transform: scale(0.7);
    opacity: 0.3;
  }
  40% {
    transform: scale(1.2) translateY(-4px);
    opacity: 1;
  }
}

/* ─── Loading Status ─── */
.ag-loading-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 2px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: var(--ag-brand);
  font-weight: 500;
  animation: agFadeIn 0.3s ease;
}

@keyframes agFadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.ag-loading-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(43, 129, 214, 0.2);
  border-top-color: var(--ag-brand);
  border-radius: 50%;
  animation: agSpin 0.8s linear infinite;
}

@keyframes agSpin {
  to { transform: rotate(360deg); }
}

/* ═══════════════════════════════════════════════
   INPUT BAR
   ═══════════════════════════════════════════════ */
.ag-input-bar {
  border-top: 1px solid var(--ag-border);
  padding: 14px 16px 10px;
  background: rgba(255, 255, 255, 0.45);
}

.ag-input-wrap {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 6px 6px 6px 16px;
  border: 1px solid var(--ag-border);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.65);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.ag-input-wrap:focus-within {
  border-color: var(--ag-brand);
  box-shadow: 0 0 0 3px rgba(43, 129, 214, 0.1);
}

.ag-input {
  flex: 1;
}

.ag-input ::v-deep .v-input__slot {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  padding: 0 !important;
  min-height: unset !important;
}

.ag-input ::v-deep .v-input__slot fieldset {
  display: none !important;
}

.ag-input ::v-deep .v-text-field__slot {
  padding: 0 !important;
}

.ag-input ::v-deep textarea {
  color: var(--ag-text) !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  padding: 6px 0 !important;
  margin: 0 !important;
}

.ag-input ::v-deep textarea::placeholder {
  color: var(--ag-text-3) !important;
}

/* ─── Send Button ─── */
.ag-send-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 11px;
  background: rgba(43, 129, 214, 0.1);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.ag-send-btn:disabled {
  cursor: default;
  opacity: 0.5;
}

.ag-send-btn--active {
  background: linear-gradient(135deg, var(--ag-brand-light), var(--ag-brand));
  box-shadow: 0 4px 14px rgba(43, 129, 214, 0.35);
  opacity: 1;
}

.ag-send-btn--active:hover {
  transform: scale(1.06);
  box-shadow: 0 6px 22px rgba(43, 129, 214, 0.4);
}

.ag-stop-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 11px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.35);
  cursor: pointer;
  transition: all 0.2s ease;
}

.ag-stop-btn:hover {
  transform: scale(1.06);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.45);
}

.ag-input-hint {
  text-align: right;
  padding: 6px 4px 0;
  font-size: 11px;
  color: var(--ag-text-3);
  opacity: 0.6;
}

/* ═══════════════════════════════════════════════
   RESPONSIVE
   ═══════════════════════════════════════════════ */
@media (max-width: 960px) {
  .ag-body {
    grid-template-columns: 1fr;
    padding: 8px;
  }
  .ag-msg-content {
    max-width: 85%;
  }
  .ag-welcome {
    padding: 32px 16px 24px;
  }
  .ag-welcome-title {
    font-size: 22px;
  }
}

@media (max-width: 600px) {
  .ag-header {
    padding: 10px 14px;
  }
  .ag-avatar {
    width: 38px;
    height: 38px;
    border-radius: 12px;
  }
  .ag-header-title {
    font-size: 15px;
  }
  .ag-messages {
    padding: 14px 10px;
  }
  .ag-msg-content {
    max-width: 90%;
    padding: 10px 12px;
  }
  .ag-msg-avatar {
    width: 28px;
    height: 28px;
    border-radius: 8px;
  }
  .ag-welcome-avatar-inner {
    width: 60px;
    height: 60px;
    border-radius: 18px;
  }
  .ag-welcome-title {
    font-size: 20px;
  }
}

/* ═══════════════════════════════════════════════
   DARK MODE
   ═══════════════════════════════════════════════ */
.ag-dark {
  --ag-brand:       #4a9ae8;
  --ag-brand-light: #6bb3f0;
  --ag-brand-dark:  #2b81d6;

  --ag-bg:          linear-gradient(145deg, #0f1724 0%, #131d2e 40%, #0d1520 100%);
  --ag-surface:     rgba(255, 255, 255, 0.06);
  --ag-surface-hover: rgba(255, 255, 255, 0.1);

  --ag-border:       rgba(74, 154, 232, 0.15);
  --ag-border-hover: rgba(74, 154, 232, 0.3);

  --ag-text:     #e1eaf4;
  --ag-text-2:   #a0bdd8;
  --ag-text-3:   #5e82a6;

  --ag-user-bubble:  rgba(74, 154, 232, 0.15);
  --ag-user-border:  rgba(74, 154, 232, 0.25);
  --ag-bot-bubble:   rgba(255, 255, 255, 0.05);
  --ag-bot-border:   rgba(74, 154, 232, 0.12);
}

.ag-dark .ag-header {
  background: rgba(15, 23, 36, 0.8);
  backdrop-filter: blur(20px) saturate(1.2);
  -webkit-backdrop-filter: blur(20px) saturate(1.2);
}

.ag-dark .ag-sidebar {
  border-right-color: var(--ag-border);
}

.ag-dark .ag-quick-btn {
  background: rgba(255, 255, 255, 0.04);
  color: var(--ag-text-2);
}

.ag-dark .ag-quick-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--ag-text);
}

.ag-dark .ag-input-bar {
  background: rgba(15, 23, 36, 0.6);
}

.ag-dark .ag-input-wrap {
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--ag-border);
}

.ag-dark .ag-input-wrap:focus-within {
  border-color: var(--ag-brand);
  box-shadow: 0 0 0 3px rgba(74, 154, 232, 0.15);
}

.ag-dark .ag-send-btn {
  background: rgba(74, 154, 232, 0.15);
}

.ag-dark .ag-orb--one {
  background: radial-gradient(circle, rgba(43, 129, 214, 0.12), transparent 70%);
}

.ag-dark .ag-orb--two {
  background: radial-gradient(circle, rgba(74, 154, 232, 0.08), transparent 70%);
}

.ag-dark .ag-orb--three {
  background: radial-gradient(circle, rgba(26, 90, 158, 0.06), transparent 70%);
}

.ag-dark .ag-msg-text ::v-deep .ag-file-card {
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--ag-border);
}

.ag-dark .ag-msg-text ::v-deep .ag-file-card-preview {
  background: #0d1520;
}

.ag-dark .ag-msg-text ::v-deep .ag-file-card-btn--download {
  background: #4a9ae8;
  color: #0f1724;
}

.ag-dark .ag-msg-text ::v-deep .ag-file-card-btn--download:hover {
  background: #6bb3f0;
}

.ag-dark .ag-msg-text ::v-deep .ag-file-card-btn--open {
  background: rgba(74, 154, 232, 0.15);
  color: #6bb3f0;
}

.ag-dark .ag-msg-text ::v-deep .ag-file-card-badge {
  background: rgba(74, 154, 232, 0.15);
  color: #6bb3f0;
}

.ag-dark .ag-msg-text ::v-deep .ag-code-block {
  background: #0a0f18;
}

.ag-dark .ag-welcome-desc {
  color: var(--ag-text-2);
}

.ag-dark .ag-welcome-chip {
  background: rgba(255, 255, 255, 0.06);
  color: var(--ag-text-2);
  border-color: var(--ag-border);
}

.ag-dark .ag-welcome-chip:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--ag-text);
}
</style>
